import { Alert, Box, Checkbox, Chip, Typography } from '@mui/material';
import { CustomButton } from '../../../common/components';
import {
  buildPaymentOrderGroups,
  getPaymentOrderStatusColor,
  getPaymentOrderStatusLabel,
} from '../utils/supplierLedgerHelpers';
import { formatAmount, formatCurrencyAmount, parseNumber } from '../utils/supplierLedgerFormatters';
import { InvoiceSummaryItem } from './SupplierLedgerShared';

const PaymentOrderProviderSelector = ({
  groups,
  applications,
  includedInvoices,
  activeProviderId,
  providerStatuses,
  providerMap,
  onSelectProvider,
  onToggleProvider,
  onWorkProvider,
}) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
    {groups.map((group, index) => {
      const included = group.invoices.filter((invoice) => includedInvoices[invoice.uuid]);
      const total = included.reduce(
        (sum, invoice) => sum + parseNumber(applications[invoice.uuid] || 0),
        0,
      );
      const totalArs = included.reduce((sum, invoice) => {
        const applied = parseNumber(applications[invoice.uuid] || 0);
        const pending = Number(invoice.pending_amount || 0);
        const pendingArs = Number(invoice.pending_amount_ars || 0);
        const rate = pending > 0 ? pendingArs / pending : 1;
        return sum + (applied * rate);
      }, 0);
      const internalOrders = buildPaymentOrderGroups(included, providerMap);
      const status = providerStatuses[group.providerId] || 'pending';
      const selected = group.providerId === activeProviderId;
      const allIncluded = included.length === group.invoices.length && group.invoices.length > 0;
      const partiallyIncluded = included.length > 0 && !allIncluded;

      return (
        <Box
          key={group.id}
          sx={{
            border: '1px solid',
            borderColor: selected ? 'primary.main' : 'divider',
            borderRadius: 1,
            backgroundColor: selected ? 'background.formTables' : 'background.paper',
            p: 1.25,
            minWidth: 0,
            cursor: 'pointer',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
              <Checkbox
                size="small"
                checked={allIncluded}
                indeterminate={partiallyIncluded}
                onChange={(event) => onToggleProvider(group.providerId, event.target.checked)}
                sx={{ p: 0, mt: -0.25 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 700, lineHeight: 1.2 }}>
                  Proveedor {index + 1}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 800,
                    lineHeight: 1.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={group.providerName}
                >
                  {group.providerName}
                </Typography>
              </Box>
            </Box>
            <Chip size="small" color={getPaymentOrderStatusColor(status)} label={getPaymentOrderStatusLabel(status)} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1, mb: 1.25 }}>
            <InvoiceSummaryItem label="Seleccionadas" value={group.invoices.length} />
            <InvoiceSummaryItem label="Incluidas" value={`${included.length}/${group.invoices.length}`} highlight />
            <InvoiceSummaryItem label="Monedas" value={group.currencies.join(' / ')} />
            <InvoiceSummaryItem
              label={internalOrders.length > 1 ? 'Total ARS' : `Total ${internalOrders[0]?.currency || 'ARS'}`}
              value={internalOrders.length > 1 ? formatAmount(totalArs) : formatCurrencyAmount(total, internalOrders[0]?.currency || 'ARS')}
              highlight
            />
          </Box>
          {internalOrders.length > 1 && (
            <Alert severity="warning" sx={{ py: 0.25, px: 1, mb: 1.25 }}>
              Se crearán {internalOrders.length} OP por moneda/cotización.
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <CustomButton
              variant={selected ? 'base' : 'outlined'}
              onClick={() => {
                onSelectProvider(group.providerId);
                onWorkProvider(group.providerId);
              }}
              disabled={!included.length}
              sx={{ minWidth: 130, height: 38 }}
            >
              Trabajar
            </CustomButton>
          </Box>
        </Box>
      );
    })}
  </Box>
);

export default PaymentOrderProviderSelector;
